<template>
	<div class="candidate-container" :data-index="candidate.id" :class="{ selected: active }">
		<img :src="thumbnail" @load="revokeObjectURL(thumbnail)" @click="select(candidate.id)" :data-index="candidate.id" />
		<div class="label-container">
			<v-text-field
				v-if="renamingFile"
				@blur="endFileRenaming"
				@keydown="renameFile"
				v-model="renamingFileName"
				autofocus
				label="File Name"
				dark
				flat
				solo-inverted
				hide-details
			></v-text-field>
			<span v-else class="name">{{ nicename(candidate.name) }}</span>

			<v-progress-circular v-if="applying" indeterminate color="var(--primary)" size="14" width="2"></v-progress-circular>
		</div>

		<v-menu offset-y :content-class="'file-options-container'">
			<template v-slot:activator="{ on }">
				<i class="material-icons" v-on="on">more_vert</i>
			</template>
			<v-list>
				<v-list-item @click="downloadLUT(candidate)" :disabled="!canExportLUT || blockLoading">
                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
					<v-list-item-title>Download a LUT</v-list-item-title>
				</v-list-item><v-list-item @click="downloadXMP(candidate)" :disabled="!canExportXMP || blockLoading">
                	<img src="/assets/icons/lr-icon-96x96.png" />
					<v-list-item-title>Download XMP Profile</v-list-item-title>
				</v-list-item>
                <v-list-item @click="downloadCO(candidate)" :disabled="!canExportCO || blockLoading">
                    <img src="/assets/icons/co-icon-96x96.png" />
                    <v-list-item-title>Download Capture One Style</v-list-item-title>
                </v-list-item>
				<v-list-item @click="applyGrade(candidate)" :disabled="blockLoading">
					<i class="material-icons">perm_media</i>
					<v-list-item-title>Apply Grade</v-list-item-title>
				</v-list-item>

                <v-list-item :disabled="!canShareAsset" @click="shareFile(candidate)">
                    <v-icon class="material-icons">mdi-share</v-icon>
                    <v-list-item-title>Share</v-list-item-title>
                </v-list-item>
<!--                <v-list-item v-else :disabled="!canShareAsset" @click="unshareFile(candidate)">-->
<!--                    <v-icon class="material-icons">block</v-icon>-->
<!--                    <v-list-item-title>Unshare</v-list-item-title>-->
<!--                </v-list-item>-->

				<v-list-item @click="startFileRenaming(candidate)" :disabled="!canRenameAsset">
					<i class="material-icons">create</i>
					<v-list-item-title>Rename</v-list-item-title>
				</v-list-item>
				<v-list-item @click="moveToTrash(candidate.id)" :disabled="!canDeleteAsset">
					<v-icon class="material-icons">mdi-delete</v-icon>
					<v-list-item-title>Delete</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import CanMixin from "@/mixins/CanMixin";
import FilenameMixin from "@/mixins/Filename";

export default {
	mixins: [CanMixin, FilenameMixin],

	data() {
		return {
			applying: false,
			renamingFile: false,
			renamingFileName: "",
			thumbnail: null,
		};
	},

	props: {
		candidate: Object,
	},

	created() {
		this.thumbnail = URL.createObjectURL(this.candidate.thumbnailBlob);
	},

	computed: {
	    ...mapGetters('Projects', ['fileHasTag']),
	    ...mapGetters('User', ['isGuest']),
        ...mapState('App', ['blockLoading']),

		active() {
			if (this.$store.state.Projects.candidate == this.candidate.id) return true;
			else return false;
		},
	},

	methods: {
		...mapActions("App", ["applyGrade"]),
		...mapActions("Helpers", ["revokeObjectURL"]),

		nicename(name) {
			return this.stripTimestampAndExtension(name);
		},

		async select(candidate_id) {
			// showing the progress before thread is blocked by applying candidate
			await new Promise(resolve => {
				this.applying = true;
				this.$store.dispatch("Loader/showProgressBar", "indeterminate");

				setTimeout(() => {
					resolve();
				}, 50);
			});

			this.$store.dispatch("Projects/setActiveCandidate", candidate_id);
			let candidate = await window.lutify.fetchCandidate(vm.$store.getters["Projects/candidateById"](candidate_id));

			window.lutify.toggleIDTODT(candidate.blocks);

			if (this.isGuest) {
			    if (!candidate.luts) {
                    candidate = await window.lutify.fetchCandidate(vm.$store.getters["Projects/candidateById"](candidate_id), true);
                }
			    candidate.blocks = candidate.blocks.filter(block => block.type == 'fixed');
                await window.lutify.applyCompare(candidate.blocks, "waitToLoad", false);
                await _Adjustments.LMT.addBlock();
                await _Adjustments.LMT.applyLMT(candidate.luts[1]);
            } else {
                if (candidate.rawDevelopment && loader.typeOfActiveMedia('raw')) {
                    vm.rawDevelopment.setValues(candidate.rawDevelopment)
                    await vm.rawDevelopment.developAndRender()
                }

                await window.lutify.applyCompare(candidate.blocks, "waitToLoad", false);
            }

			this.applying = false;
			this.$store.dispatch("Loader/showProgressBar", false);

			window.UI.removeFileLoadingSpinner(candidate_id);
			// UI.removeCandidateLoadingSpinner();
			window.UI.hideImageLoader();
			window.UI.toggleImageTools();

			// NOTE implement to update by checking if flagged
			if (this.canUseColorGradingTools) {
				let thumbnailBlob = await window.lutify.updateCandidateThumbnail(candidate);
				this.thumbnail = URL.createObjectURL(thumbnailBlob);
				// console.info(`[CREATE URL][CANDIDATE] ${this.thumbnail}`);

				// NOTE: maybe no need for this
				this.$store.dispatch("UI/updateCandidateThumbnail", {
					id: candidate_id,
					thumbnailBlob,
				});
			}
		},

		async moveToTrash(candidate_id) {
			// await this.$store.dispatch("Projects/selectBin", file.bin_id);
			let selectedFiles = [{ id: candidate_id }];

			this.$store.dispatch("Loader/showProgressBar", "indeterminate");

			for await (const selectedFile of selectedFiles) {
				let isUploading = await window.uploadCandidatesStore.getItem(selectedFile.id);
				if (isUploading) {
					alert("Candidate can't be moved while uploading to server. Try again later");
					this.$store.dispatch("Loader/showProgressBar", false);
					return;
				}

				window.candidatesStore.removeItem(selectedFile.id);
				await this.$store.dispatch("UI/removeCandidate", selectedFile.id);

				window
					.$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
					.closest(".v-treeview-node__root")
					.remove();
			}

			await this.$store.dispatch("Projects/moveTo", {
				file_id: selectedFiles[0].id,
				action: "Trash",
				file_ids: selectedFiles.map(e => e.id),
			});

			await this.$store.dispatch("Projects/refreshProjectTree");
			this.$store.dispatch("Loader/showProgressBar", false);
		},

		startFileRenaming(file) {
			this.$store.dispatch("Projects/setAction", {
				type: "file",
				action: "file-rename",
				data: file,
			});

			this.renamingFile = file.id;
			this.renamingFileName = this.stripTimestampAndExtension(file.name);
		},

		async renameFile(e) {
			if (e.key == "Enter" || e.keyCode == 13) {
				this.renamingFile = false;

				let oldFilename = this.$store.state.Projects.action.data.name;
				let fileType = oldFilename.substring(oldFilename.lastIndexOf(".") + 1);
				let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

				if (this.stripTimestampAndExtension(oldFilename) !== this.renamingFileName) {
					await this.$store.dispatch("Projects/updateFileName", {
						name: newFilename,
					});

					await this.$store.dispatch("UI/renameCandidate", {
						id: this.$store.state.Projects.action.data.id,
						name: newFilename,
						nicename: this.stripTimestampAndExtension(newFilename),
					});

					await this.$store.dispatch("Projects/refreshProjectTree", {
						createPresignedURL: true,
					});

					let candidateStoreData = await candidatesStore.getItem(this.$store.state.Projects.action.data.id);
					if (candidateStoreData)
						candidatesStore.setItem(candidateStoreData.id, { ...candidateStoreData, name: newFilename, nicename: this.renamingFileName });
				}

				this.endFileRenaming();
			} else if (e.key == "Escape" || e.keyCode == 27) {
				this.endFileRenaming();
			}
		},

		endFileRenaming() {
			this.renamingFile = false;
			this.$store.dispatch("Projects/setAction", {
				type: "file",
				action: "file-rename-end",
				data: null,
			});
		},

		async downloadLUT(candidate) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

			let candidateStoreData = await window.lutify.fetchCandidate(vm.$store.getters["Projects/candidateById"](candidate.id));

			await this.$store.dispatch("Projects/setAction", {
				type: "candidate",
				action: "export-lut-from-asset",
				data: [candidateStoreData],
			});

            this.$store.dispatch('UI/setRightColumn', 'download-lut');
		},

		async downloadXMP(candidate) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let candidateStoreData = await window.lutify.fetchCandidate(vm.$store.getters["Projects/candidateById"](candidate.id));

            await this.$store.dispatch("Projects/setAction", {
                type: "candidate",
                action: "export-xmp-from-asset",
                data: [candidateStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
		},

		async downloadCO(candidate) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let candidateStoreData = await window.lutify.fetchCandidate(vm.$store.getters["Projects/candidateById"](candidate.id));

            await this.$store.dispatch("Projects/setAction", {
                type: "candidate",
                action: "export-co-from-asset",
                data: [candidateStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
		},

        async shareFile(file) {
            await this.$store.dispatch("Projects/addTag", {
                file_ids: [file.id],
                tag_name: "shared",
                tag_value: "shared",
            });

            window.UI.message.activate(`Item added to Shared folder`);

            await this.$store.dispatch("Projects/refreshProjectTree");
        },

        // async unshareFile(file) {
        //     await this.$store.dispatch("Projects/removeTag", {
        //         file_ids: [file.id],
        //         tag_name: "shared",
        //         tag_value: "shared",
        //     });
        //
        //     window.UI.message.activate(`Item removed from Shared folder`);
        //
        //     await this.$store.dispatch("Projects/refreshProjectTree");
        // },
	},
};
</script>

<style lang="scss">
.candidate-container {
	display: grid;
	grid-template-areas: "image image" "label delete-btn";
	grid-template-columns: 1fr 20px;
	grid-row-gap: 5px;
	align-items: center;
	align-content: center;
	&:not(:last-of-type) {
		margin-bottom: 20px;
	}

	> img {
        width: 100%;
        grid-area: image;
		cursor: pointer;
		border: 2px solid transparent;
		border-radius: var(--thumbnail-lg-border-radius);
		&:hover {
			border: 2px solid var(--primary);
		}
	}

	&.selected > img {
		border: 2px solid var(--primary);
	}

	> .label-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		grid-area: label;
		overflow: hidden;

		> .name {
			color: var(--font-default-color);
			font-size: 0.9rem;
			// padding-right: 25px;
			// text-transform: capitalize;
			text-align: left;
			line-height: 0.9rem;
			height: 100%;
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
			margin-top: 2px;
			width: 87%;
		}

		> .v-progress-circular {
			margin-left: 10px;
			margin-right: 5px;
		}

		.v-input {
			font-size: 12px;
			.v-input__control {
				min-height: initial;
				// height: 10px;
				input {
					line-height: 1;
					color: var(--font-default-color) !important;
					padding: 0;
					text-overflow: ellipsis;
					overflow: hidden;
					white-space: nowrap;
				}

				.v-input__slot {
					padding: 0;
					background: transparent;
				}
			}

			&--is-focused {
				.v-input__slot {
					// background: #363636;
					background: transparent;
				}
			}
		}
	}

	i.material-icons {
		font-size: 18px;
		cursor: pointer;
	}
}
</style>
